import { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from '../axios-config';
import { generateCouponBookPDF } from '../components/Acorn/CouponBookPDF';
import type { Acorn } from '../models/acorn';
import { useBadges } from '../contexts/BadgeContext';
import { UserContext } from '../contexts/UserContext';

type CouponAcorn = {
  acornId?: number;
  code: string;
  points: number;
  acornDescription?: string;
  acornHistories?: any[];
  // Optional badge linkage shapes
  acornBadgeXps?: Array<{ badge?: { badgeName?: string }; badgeName?: string }>;
  badge?: { badgeName?: string };
  badgeName?: string;
};

export default function CouponBookPage() {
  const { badges } = useBadges();
  const { userProfile } = useContext(UserContext);
  const [params] = useSearchParams();
  const [acorns, setAcorns] = useState<CouponAcorn[] | null>(null);
  const [error, setError] = useState<string>('');
  const [opening, setOpening] = useState<boolean>(false);

  const prefix = params.get('prefix')?.toUpperCase().trim();
  const codesParam = params.get('codes'); // comma-separated codes optional

  const codes = useMemo(() => {
    if (!codesParam) return [] as string[];
    return codesParam
      .split(',')
      .map(s => s.trim().toUpperCase())
      .filter(Boolean);
  }, [codesParam]);

  useEffect(() => {
    let mounted = true;
    const fetchAndOpen = async () => {
      try {
        // If data was passed via sessionStorage, prefer that.
        const stored = sessionStorage.getItem('acorn:couponbook:data');
        if (stored) {
          const parsed: CouponAcorn[] = JSON.parse(stored);
          // Only include those created by the current user
          const userId = userProfile?.userId;
          const owned = userId
            ? parsed.filter(a => Array.isArray(a.acornHistories) && a.acornHistories.some((h: any) => String(h.createdBy) === String(userId)))
            : parsed;
          const enriched = await enrichWithBadgeInfo(owned);
          if (mounted) setAcorns(enriched);
          await openPdf(enriched);
          return;
        }

        const { data } = await axios.get<CouponAcorn[]>('/api/acorns', { withCredentials: true });
        let selected: CouponAcorn[] = data || [];
        // Filter to only coupons created by the current user (check acornHistories[].createdBy)
        const userId = userProfile?.userId;
        if (userId) {
          selected = selected.filter(a => Array.isArray((a as any).acornHistories) && (a as any).acornHistories.some((h: any) => String(h.createdBy) === String(userId)));
        }
        if (prefix) {
          selected = selected.filter((a: CouponAcorn) => String(a.code || '').toUpperCase().startsWith(prefix));
        }
        if (codes.length) {
          const setCodes = new Set(codes);
          selected = selected.filter((a: CouponAcorn) => setCodes.has(String(a.code || '').toUpperCase()));
        }
        if (!selected.length) {
          setError('No coupons matched. Provide ?prefix=ABC or ?codes=A1B2C3,...');
          setAcorns([]);
          return;
        }
        const enriched = await enrichWithBadgeInfo(selected);
        if (mounted) setAcorns(enriched);
        await openPdf(enriched);
      } catch (err) {
        console.error('Failed to load coupons for couponbook:', err);
        setError('Failed to load coupons.');
      }
    };
    fetchAndOpen();
    return () => { mounted = false; };
  }, [prefix, codes]);

  const openPdf = async (items: CouponAcorn[]) => {
    setOpening(true);
    try {
      const blob = (await generateCouponBookPDF(items as unknown as Acorn[], { returnBlob: true, badges })) as Blob;
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setOpening(false);
    }
  };

  // Attach friendly badgeName and a usable image url to each acorn if we can resolve it
  const enrichWithBadgeInfo = async (arr: CouponAcorn[]): Promise<CouponAcorn[]> => {
    // Ensure we have badge definitions; if BadgeContext is empty, fetch as a fallback
    let badgeDefs = badges;
    if (!badgeDefs || !badgeDefs.length) {
      try {
        const res = await axios.get('/api/badges', { withCredentials: true });
        badgeDefs = res.data || [];
      } catch (err) {
        // If we can't fetch badges, return original array unchanged
        return arr;
      }
    }

  const byId = new Map<string, typeof badgeDefs[number]>();
  badgeDefs.forEach((b: any) => byId.set(String(b.id || b.badgeId || '').toLowerCase(), b));

    const allBadgeIdsFrom = (a: any): string[] => {
      const ids: string[] = [];
      if (a?.badgeId) ids.push(String(a.badgeId));
      if (a?.badge?.badgeId) ids.push(String(a.badge.badgeId));
      if (Array.isArray(a?.acornBadgeXps)) {
        for (const entry of a.acornBadgeXps) {
          if (entry?.badgeId) ids.push(String(entry.badgeId));
          if (entry?.badge?.badgeId) ids.push(String(entry.badge.badgeId));
        }
      }
      return ids;
    };

    return arr.map(a => {
      const ids = allBadgeIdsFrom(a).map(id => id.toLowerCase());
      const matched = ids.map(id => byId.get(id)).find(Boolean) as any | undefined;
      if (!matched) return a;
      const levelImage = Object.values(matched.levelImages || {})[0] as string | undefined;
      const badgeImageUrl = levelImage || matched.badgeImageUrl || matched.levelImageUrl;
      // Also enrich nested acornBadgeXps with badge names for PDF header building
      let acornBadgeXps = Array.isArray((a as any).acornBadgeXps) ? (a as any).acornBadgeXps : undefined;
      if (acornBadgeXps) {
        acornBadgeXps = acornBadgeXps.map((x: any) => {
          const key = String(x?.badgeId || '').toLowerCase();
          const b = key ? byId.get(key) as any : undefined;
          return {
            ...x,
            badgeName: x?.badgeName || x?.badge?.badgeName || b?.badgeName || b?.name,
          };
        });
      }
      return {
        ...a,
        badgeName: matched.name || matched.badgeName || matched.badge?.badgeName,
        badgeImageUrl,
        acornBadgeXps,
      } as CouponAcorn;
    });
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Coupon Book</h1>
      <p className="text-gray-600 mb-4">This page automatically opens a printable PDF with your coupons.</p>
      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded p-3 mb-4">{error}</div>
      )}
      <div className="bg-white rounded border p-4">
        <p className="text-sm text-gray-700 mb-2">
          If a new tab didn’t open, your browser may have blocked it. You can manually open it below.
        </p>
        <button
          disabled={!acorns?.length || opening}
          onClick={() => acorns && openPdf(acorns)}
          className={`px-4 py-2 rounded text-white text-sm ${opening ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
        >
          {opening ? 'Opening…' : 'Open PDF'}
        </button>
        <div className="text-xs text-gray-500 mt-3">
          Usage: /couponbook?prefix=ABC or /couponbook?codes=ABC123,XYZ789
        </div>
      </div>
    </div>
  );
}
